import api from './config';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';

const initialState = {
  abonnement: [],
  selected_abonnement: null,
  loading: false,
  isSuccess: false,
  errors: [],
  recordsTotal: 0
};

export const fetchAbonnements = createAsyncThunk(
  'abonnement/fetchAbonnements',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      // ✅ CORRECTION: Changed from '/abnonement' to '/abonnement'
      const response = await api.get('/abonnement', { params });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const addAbonnement = createAsyncThunk(
  'abonnement/addAbonnement',
  async (abonnementData, { rejectWithValue, dispatch }) => {
    try {
      const params = {
        title: abonnementData.title,
        little_description: abonnementData.little_description,
        priceTND: abonnementData.priceTND,
        priceUSD: abonnementData.priceUSD,
        priceSAR: abonnementData.priceSAR,
        details: abonnementData.details,
        active: abonnementData.active,
        type: abonnementData.type,
        monthlyDiscount: abonnementData.monthlyDiscount,
        quarterlyDiscount: abonnementData.quarterlyDiscount,
        semiAnnualDiscount: abonnementData.semiAnnualDiscount,
        annualDiscount: abonnementData.annualDiscount,
        monthly: abonnementData.monthly,
        quarterly: abonnementData.quarterly,
        semiAnnual: abonnementData.semiAnnual,
        annual: abonnementData.annual,
        features: abonnementData.features,
        isTrialPack: abonnementData.isTrialPack,
      };
      const response = await api.post('/abonnement/add', params);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

// Fonctionnalités cochables sur un pack + celles de l'essai quand aucun pack n'est « pack d'essai ».
export const fetchPackFeatures = createAsyncThunk(
  'abonnement/fetchPackFeatures',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/abonnement/features');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

// Le back-end ne garde qu'un pack d'essai : on reflète tout de suite le décochage des autres.
const keepSingleTrialPack = (state, trialPackId) => {
  state.abonnement = state.abonnement.map((abonnement) =>
    abonnement._id !== trialPackId && abonnement.DT_RowId !== trialPackId && abonnement.isTrialPack
      ? { ...abonnement, isTrialPack: false }
      : abonnement
  );
};

export const getAbonnementById = createAsyncThunk(
  'abonnement/getAbonnementById',
  async (abonnementId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/abonnement/${abonnementId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateAbonnementById = createAsyncThunk(
  'abonnement/updateAbonnementById',
  async (updatedAbonnement, { rejectWithValue, dispatch }) => {
    try {
      const { 
        id, 
        title, 
        little_description, 
        priceTND, 
        priceUSD,
        priceSAR,
        details,
        active, 
        type, 
        monthly, 
        quarterly, 
        semiAnnual, 
        annual, 
        monthlyDiscount, 
        quarterlyDiscount, 
        semiAnnualDiscount, 
        annualDiscount,
        features,
        isTrialPack
      } = updatedAbonnement;

      const params = {
        id,
        title,
        little_description,
        priceTND,
        priceUSD,
        priceSAR,
        details,
        active: Boolean(active),
        type,
        monthlyDiscount,
        quarterlyDiscount,
        semiAnnualDiscount,
        annualDiscount,
        monthly,
        quarterly,
        semiAnnual,
        annual,
        // Absents = inchangés côté back-end (ancien pack non configuré).
        features,
        isTrialPack
      };
      
      const response = await api.put(`/abonnement/${id}`, params);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

// Refusé (409) par le back-end si des entreprises ont déjà souscrit à ce plan.
export const deleteAbonnement = createAsyncThunk(
  'abonnement/deleteAbonnement',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/abonnement/${id}`);
      return id;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

const subscriptionSlice = createSlice({
  name: 'abonnement',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAbonnements.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAbonnements.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.abonnement = payload.data || [];
        state.recordsTotal = payload.recordsTotal || 0;
        state.loading = false;
      })
      .addCase(fetchAbonnements.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      })
      .addCase(addAbonnement.pending, (state) => {
        state.loading = true;
      })
      .addCase(addAbonnement.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        const newAbonnement = { ...payload, _DT_RowId: payload._id };
        if (payload?.isTrialPack) keepSingleTrialPack(state, payload._id);
        state.abonnement.push(newAbonnement);
        state.loading = false;
      })
      .addCase(addAbonnement.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      })
      .addCase(updateAbonnementById.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAbonnementById.fulfilled, (state, { payload }) => {
        state.abonnement = state.abonnement.map((abonnement) =>
          abonnement._id === payload._id
            ? { ...abonnement, ...payload }
            : abonnement
        );
        if (payload?.isTrialPack) keepSingleTrialPack(state, payload._id);
        state.loading = false;
      })
      .addCase(updateAbonnementById.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      })
      .addCase(deleteAbonnement.fulfilled, (state, { payload }) => {
        state.abonnement = state.abonnement.filter(
          (abonnement) => abonnement.DT_RowId !== payload && abonnement._id !== payload
        );
        state.recordsTotal = Math.max(0, state.recordsTotal - 1);
      })
      .addCase(getAbonnementById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAbonnementById.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.selected_abonnement = payload;
        state.loading = false;
      })
      .addCase(getAbonnementById.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      });
  }
});

export default subscriptionSlice.reducer;
